import { useState } from "react";
import { Mail, RefreshCw, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";


export const VerificationRequired = ({ email, onClose }) => {
  const { resendVerificationEmail } = useAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // 'success', 'error', null
  const [resendMessage, setResendMessage] = useState("");

  const handleResendVerification = async () => {
    if (!email) return;

    setIsResending(true);
    setResendStatus(null);
    setResendMessage("");

    try {
      const result = await resendVerificationEmail(email);

      if (result.success) {
        setResendStatus("success");
        setResendMessage(result.message || "New verification email sent! Please check your email.");
      } else {
        setResendStatus("error");
        setResendMessage(result.message || "Failed to resend verification email. Please try again.");
      }
    } catch (error) {
      console.error("Resend error:", error);
      setResendStatus("error");
      setResendMessage("Failed to resend verification email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-lg">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
            <Mail className="w-8 h-8 text-orange-600" />
          </div>
          
          <h2 className="text-xl font-bold mb-2">Email Verification Required</h2>
          <p className="text-muted-foreground mb-4">
            Please verify your email address to access your account features.
          </p>
          
          {email && (
            <p className="text-sm text-muted-foreground mb-6">
              We sent a verification email to: <strong>{email}</strong>
            </p>
          )}

          {resendStatus && (
            <div className={`flex items-center gap-2 p-3 text-sm mb-4 rounded-md ${
              resendStatus === 'success' 
                ? 'text-green-600 bg-green-50 border border-green-200' 
                : 'text-red-600 bg-red-50 border border-red-200'
            }`}>
              {resendStatus === 'success' ? (
                <CheckCircle size={16} />
              ) : (
                <AlertCircle size={16} />
              )}
              <span>{resendMessage}</span>
            </div>
          )}
          
          <div className="space-y-3">
            <Button 
              onClick={handleResendVerification}
              disabled={isResending || resendStatus === 'success'}
              className="w-full bg-igbanwo-mint hover:bg-igbanwo-mint/90 text-white"
            >
              {isResending ? (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Sending...
                </div>
              ) : resendStatus === 'success' ? (
                "Email Sent!"
              ) : (
                "Resend Verification Email"
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={onClose}
              className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Close
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            Check your spam folder if you don't see the email.
          </p>
        </div>
      </div>
    </div>
  );
};